<script lang="ts" setup>
import { onMounted, onUnmounted, ref } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useNotificationsStore } from '@/store/notifications'
import { usePayrollChange } from '@/app/core/composable/routes/useChangePayroll'
import { notificationError } from '@/app/core/util/notificationUtils'
import { useI18n } from 'vue-i18n'
import EmployeeLiquidation from '../component/EmployeeLiquidation.vue'
import {
  getLiquidationRequest,
  getMovements,
  getConcepts,
  updateLiquidationRequestEmployee,
  deleteLiquidationRequestEmployee,
  reprocessLiquidationRequestEmployee,
  closeLiquidationRequest,
  generateLiquidationReport,
  getReportRequest,
  getLiquidationStatus
} from '@/app/core/services/liquidation'
import { DBtn, DLoading, DModal } from 'front-library'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
dayjs.extend(utc)
usePayrollChange(() =>
  Boolean(route.params.id) ? back() : window.location.reload()
)

/* router, notifications */
const router = useRouter()
const route = useRoute()
const notifications = useNotificationsStore()
const { t } = useI18n()

/* Datos componente Modal y Loading */
const showModal = ref(false)
const loading = ref(false)

const showModalReport = ref(false)
const titleModal = ref('')
const descriptionModal = ref('')
const typeAlertModal = ref('')

/* Datos */
const data = ref()
const indexEmployee = ref(null)
const itemsAdditionalConcepts = ref<any[]>([])
const urlFile = ref(null)
const refresh = ref()

/* Consultar estado de la solicitud de liquidacion */
const getLiquidation = async () => {
  try {
    data.value = await getLiquidationRequest(route.params.id as string)
    if (data.value === undefined) {
      notifications.addNotification({
        title: t('notification.notFound'),
        text: t('liquidations.notification.error'),
        type: 'error',
        time: 10000
      })
      back()
    }
    loading.value = false
  } catch (error: any) {
    notificationError(error, 'liquidations', t, notifications, loading)
  }
}

/* Actualizar informacion de un empleado */
const updateEmployee = async (dataUpdate: any) => {
  await updateLiquidationRequestEmployee(
    route.params.id as string,
    dataUpdate.idEmployee,
    dataUpdate.data
  )
    .then(async (response: any) => {
      if (response) {
        data.value.employees[dataUpdate.index].concepts = await getMovements(
          route.params.id as string,
          dataUpdate.idEmployee
        )
        indexEmployee.value = dataUpdate.index
        setTimeout(() => {
          indexEmployee.value = null
        }, 2000)
      }
    })
    .catch((error: any) => {
      notificationError(error, 'liquidations', t, notifications, loading)
    })
}

/* Movimientos del empleados */
const getMovementsEmployee = async (
  employeeId: string,
  index: any,
  iconReProcess: any
) => {
  try {
    const [movementsResult, liquidationResult] = await Promise.all([
      getMovements(route.params.id as string, employeeId),
      getLiquidationRequest(route.params.id as string)
    ])
    data.value.employees[index].concepts = movementsResult
    data.value.employees[index].isRecontractable =
      liquidationResult.employees.filter(
        (item: any) => item.id === employeeId
      )[0].isRecontractable
    data.value.employees[index].observation =
      liquidationResult.employees.filter(
        (item: any) => item.id === employeeId
      )[0].observation
  } catch (error: any) {
    notificationError(error, 'liquidations', t, notifications, loading)
    notifications.addNotification({
      title: t('liquidations.notification.recommendation'),
      text: t('liquidations.notification.toReprocess'),
      type: 'warning',
      time: 10000
    })
    iconReProcess?.classList.remove('hidden')
  }
}
const checkLiquidationStatus = async (
  eyeButton: any,
  reprocessButton: any,
  trashButton: any,
  printButton: any,
  loadingIcon: any,
  isReprocess?: boolean
) => {
  const checking = await getLiquidationStatus(route.params.id as string)
  if (!checking || checking.status === 'ERROR') {
    reprocessButton?.classList.remove('hidden')
    notifications.addNotification({
      title: t('liquidations.notification.recommendation'),
      text: t('liquidations.notification.toReprocess'),
      type: 'warning',
      time: 10000
    })
  }

  if (checking.status === 'COMPLETE') {
    eyeButton.classList.remove('hidden')
    trashButton.classList.remove('hidden')
    printButton.classList.remove('hidden')

    loadingIcon.classList.add('hidden')

    if (isReprocess) {
      notifications.addNotification({
        title: t('liquidations.notification.reprocessed'),
        text: t('liquidations.notification.employeeProcessedCompleted'),
        type: 'success',
        time: 15000
      })
    }
  }

  if (checking.status === 'PENDING' || checking.status === 'IN_PROGRESS') {
    setTimeout(() => {
      checkLiquidationStatus(eyeButton, reprocessButton, trashButton, printButton, loadingIcon, true)
    }, 2000)
  }
}

/* Reprocesar empleado */
const reprocessEmployee = async (
  employeeId: string,
  eyeButton: any,
  reprocessButton: any,
  trashButton: any,
  printButton: any,
  loadingIcon: any
) => {
  loading.value = true
  await reprocessLiquidationRequestEmployee(
    route.params.id as string,
    employeeId
  )
    .then(async (response: any) => {
      void getLiquidation()
      void checkLiquidationStatus(eyeButton, reprocessButton, trashButton, printButton, loadingIcon)
      notifications.addNotification({
        title: t('liquidations.notification.reprocessed'),
        text: t('liquidations.notification.employeeProcessed'),
        type: 'success',
        time: 15000
      })
    })
    .catch((error: any) => {
      notificationError(error, 'liquidations', t, notifications, loading)
    })
}

/* Eliminar empleado */
const removeEmployee = async (employeeId: string) => {
  loading.value = true
  await deleteLiquidationRequestEmployee(route.params.id as string, employeeId)
    .then(async (response: any) => {
      void getLiquidation()
      notifications.addNotification({
        title: t('liquidations.notification.deleted'),
        text: t('liquidations.notification.employeeDeleted'),
        type: 'success',
        time: 10000
      })
    })
    .catch((error: any) => {
      notificationError(error, 'liquidations', t, notifications, loading)
    })
}

/* Cerrar liquidacion */
const closeLiquidation = async () => {
  loading.value = true
  showModal.value = false
  await closeLiquidationRequest(route.params.id as string)
    .then(response => {
      loading.value = false
      notifications.addNotification({
        title: t('liquidations.notification.closed'),
        text: t('liquidations.notification.terminationClosed'),
        type: 'success',
        time: 10000
      })
      back()
    })
    .catch((error: any) => {
      notificationError(error, 'liquidations', t, notifications, loading)
    })
}

/* Volver a la lista */
const back = () => {
  void router.push({ name: 'ListLiquidationRequests' })
}

/* Cerrar el componente Modal */
const closeModal = () => {
  showModal.value = false
  showModalReport.value = false
}

const generateReport = async (values: any) => {
  loading.value = true
  try {
    if (Boolean(values.parameters.templateId)) {
      const response = await generateLiquidationReport(values)
      reportRequest(response)
    } else {
      loading.value = false
      notifications.addNotification({
        title: t('status.ERROR'),
        text: t('liquidations.notification.noTemplate'),
        type: 'error',
        time: 10000
      })
    }
  } catch (error: any) {
    notificationError(error, 'liquidations', t, notifications, loading)
  }
}

const reportRequest = (id: string) => {
  urlFile.value = null
  try {
    refresh.value = setInterval(async () => {
      const response = await getReportRequest(id)
      if (response.status === 'Completado') {
        urlFile.value = response.url
        descriptionModal.value = ''
        typeAlertModal.value = 'success'
        titleModal.value = t('reports.notification.reportGenerated')
        showModalReport.value = true
        loading.value = false
        clearInterval(refresh.value)
      } else if (response.status === 'Error') {
        typeAlertModal.value = 'error'
        titleModal.value = t('reports.notification.reportNoGenerated')
        if (response.errors.length === 1) {
          descriptionModal.value = response.errors[0]
        } else {
          for (let i = 0; i < response.errors.length; i++) {
            const error = response.errors[i]
            descriptionModal.value += error
            if (i < response.errors.length - 1) {
              descriptionModal.value += '<br>'
            }
          }
        }
        showModalReport.value = true
        loading.value = false
        clearInterval(refresh.value)
      }
    }, 5000)
  } catch (error: any) {
    notificationError(error, 'liquidations', t, notifications, loading)
  }
}

onMounted(async () => {
  loading.value = true
  try {
    itemsAdditionalConcepts.value = await getConcepts()
    void (await getLiquidation())
  } catch (error: any) {
    notificationError(error, 'liquidations', t, notifications, loading)
  }
})

onUnmounted(() => {
  clearInterval(refresh.value)
})
</script>
<template>
  <DModal
    :open="showModal"
    typeAlert="question"
    :title="t('liquidations.list.confirmClose')"
    :description="t('modal.description')"
    :acceptQuestion="
      t('modal.acceptQuestion', {
        action: t('liquidations.list.close')
      })
    "
    @closeModal="closeModal"
    @otherMethod="closeLiquidation" />

  <DModal
    :open="showModalReport"
    :typeAlert="typeAlertModal"
    :title="titleModal"
    :description="descriptionModal"
    @closeModal="closeModal">
    <template v-slot:description v-if="urlFile != null">
      <a
        class="underline tracking-widest text-blue-600"
        :href="urlFile"
        target="_blank"
        v-html="t('reports.notification.viewFile')" />
    </template>
  </DModal>
  <DLoading v-show="loading" :message="t('loading.data')" />
  <div class="h-full px-5 pb-5">
    <div class="bg-gray-50 sm:pb-5 lg:pb-12">
      <div class="leading-4 pt-responsive">
        <p
          class="text-3xl font-semibold text-shadow text-blue-900"
          v-if="data && data.status_code">
          {{
            data.status_code === 'PENDING'
              ? t('liquidations.close.inProcess')
              : data.status_code === 'ERROR'
              ? t('liquidations.close.error')
              : t('liquidations.close.completed')
          }}
        </p>
      </div>
    </div>
    <div style="margin-top: -17px">
      <div class="flex flex-col">
        <div
          class="p-3 mx-3 mt-3 mb-4 rounded-xl"
          style="box-shadow: 1px 2px 17px 2px rgb(0 0 0 / 9%)">
          <div class="flex justify-between gap-4">
            <div>
              <div>
                <p class="mb-1.5">
                  <b>{{ t('liquidations.close.basicInfo') }}</b>
                </p>
                <div class="flex items-center gap-4">
                  <p class="size">
                    {{ t('liquidations.close.frequency') }}
                    <span class="font-semibold" v-if="data && data.frequency">{{
                      data.frequency
                    }}</span>
                  </p>
                  <p class="size">
                    {{ t('liquidations.close.currency') }}
                    <span class="font-semibold" v-if="data && data.currency">{{
                      data.currency
                    }}</span>
                  </p>
                  <p class="size">
                    {{ t('liquidations.close.reason') }}
                    <span class="font-semibold" v-if="data && data.leave_reason"
                      >{{ data.leave_reason }}
                    </span>
                  </p>
                  <p class="size">
                    {{ t('liquidations.close.exitDate') }}
                    <span class="font-semibold" v-if="data && data.leave_date">
                      {{ dayjs.utc(data.leave_date).format('DD/MM/YYYY') }}
                    </span>
                  </p>
                  <div
                    v-if="data && data.status_code"
                    class="ml-2 font-semibold text-sm py-0.5 px-3 rounded-full"
                    :class="
                      data.status_code === 'PENDING'
                        ? 'bg-blue-100 text-blue-600'
                        : data.status_code === 'ERROR'
                        ? 'bg-red-100 text-red-600'
                        : 'bg-green-100 text-green-600'
                    ">
                    {{ t('status.' + data.status_code) }}
                  </div>
                </div>
              </div>
            </div>
            <div class="flex items-center" v-if="data && data.status_code">
              <DBtn
                v-if="
                  data.status_code === 'PENDING' || data.status_code === 'ERROR'
                    ? true
                    : false
                "
                @click="showModal = true"
                class="mr-2 px-4 py-2 font-semibold"
                v-html="t('liquidations.list.close')" />
              <DBtn
                type="button"
                color="secondary"
                @click="back"
                class="px-4 py-2 font-semibold"
                v-html="t('form.button.back')" />
            </div>
          </div>
        </div>
        <div
          v-if="data"
          class="scroll overflow-auto"
          style="height: calc(100vh - 15rem)">
          <div v-for="(employee, index) in data.employees" :key="index">
            <EmployeeLiquidation
              :index="index"
              :employee="employee"
              :liquidation="data"
              :itemsAdditionalConcepts="itemsAdditionalConcepts"
              :indexEmployee="indexEmployee === index"
              @check:liquidation="checkLiquidationStatus"
              @remove:employee="removeEmployee"
              @get:movements="getMovementsEmployee"
              @update:employee="updateEmployee"
              @reprocess:employee="reprocessEmployee"
              @liquidationReport:employee="generateReport" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<style scoped>
.size {
  font-size: 15px;
  line-height: 1.3;
}
</style>
